/**
 * Application root.
 *
 * Routing is a small hash-based router for now: TanStack Router file routes
 * arrive with the detail pages in the next phase, and adding it before there
 * are nested routes to justify it would be scaffolding for its own sake.
 * URL-driven navigation is in place from the start either way, because
 * linkable views are a real requirement (staff share filtered links).
 */
import { QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AppShell } from "@/components/layout/app-shell";
import { LoginPage } from "@/features/auth/login-page";
import {
  VerifyPage,
  verificationIdFromPath,
} from "@/features/registers/verify-page";
import { NoAccessState } from "@/components/patterns/states";
import { Toaster } from "@/components/patterns/toaster";
import {
  SessionProvider,
  useSession,
  type Permission,
} from "@/lib/auth/session";
import { createQueryClient } from "@/lib/query";

/**
 * Pages load on the route that needs them.
 *
 * The whole product was one bundle: opening the dashboard downloaded the
 * survey forms, the certificate dialogs and the finance screens with it. That
 * matters here more than most places -- this runs on modest Android phones
 * over 4G in Kerala, and the budget is 200KB gzipped for the initial load.
 *
 * `LoginPage` and `VerifyPage` are deliberately NOT lazy: they are the two
 * screens a first-time visitor sees, and splitting them would add a round
 * trip to exactly the moment there is nothing on screen yet.
 */
const AreasPage = lazy(() =>
  import("@/features/community/pages").then((m) => ({ default: m.AreasPage })),
);
const HousesPage = lazy(() =>
  import("@/features/community/pages").then((m) => ({ default: m.HousesPage })),
);
const FamiliesPage = lazy(() =>
  import("@/features/community/pages").then((m) => ({
    default: m.FamiliesPage,
  })),
);
const PeoplePage = lazy(() =>
  import("@/features/community/pages").then((m) => ({ default: m.PeoplePage })),
);
const PersonDetailPage = lazy(() =>
  import("@/features/community/detail-pages").then((m) => ({
    default: m.PersonDetailPage,
  })),
);
const HouseDetailPage = lazy(() =>
  import("@/features/community/detail-pages").then((m) => ({
    default: m.HouseDetailPage,
  })),
);
const FamilyDetailPage = lazy(() =>
  import("@/features/community/detail-pages").then((m) => ({
    default: m.FamilyDetailPage,
  })),
);
const RegistersPage = lazy(() =>
  import("@/features/registers/pages").then((m) => ({
    default: m.RegistersPage,
  })),
);
const RegisterDetailPage = lazy(() =>
  import("@/features/registers/pages").then((m) => ({
    default: m.RegisterDetailPage,
  })),
);
const MahalluDashboardPage = lazy(() =>
  import("@/features/dashboards/pages").then((m) => ({
    default: m.MahalluDashboardPage,
  })),
);
const ZakatDashboardPage = lazy(() =>
  import("@/features/dashboards/pages").then((m) => ({
    default: m.ZakatDashboardPage,
  })),
);
const PalliativeDashboardPage = lazy(() =>
  import("@/features/dashboards/pages").then((m) => ({
    default: m.PalliativeDashboardPage,
  })),
);
const RevenuePage = lazy(() =>
  import("@/features/dashboards/pages").then((m) => ({
    default: m.RevenuePage,
  })),
);
const CollectionPage = lazy(() =>
  import("@/features/finance/pages").then((m) => ({
    default: m.CollectionPage,
  })),
);
const SurveyPage = lazy(() =>
  import("@/features/welfare/pages").then((m) => ({ default: m.SurveyPage })),
);
const PublicSurveyForm = lazy(() =>
  import("@/features/welfare/public-survey").then((m) => ({
    default: m.PublicSurveyForm,
  })),
);

/**
 * `/survey/<token>` -- the path the shared link uses.
 *
 * Only ever one segment after /survey/, which is what keeps this from
 * shadowing anything: the staff queue lives at the hash route #/survey and
 * leaves `pathname` as "/".
 */
function surveyTokenFromPath(pathname: string): string | null {
  const match = /^\/survey\/([^/]+)\/?$/.exec(pathname);
  return match ? decodeURIComponent(match[1]!) : null;
}
const MadrasaPage = lazy(() =>
  import("@/features/welfare/pages").then((m) => ({ default: m.MadrasaPage })),
);
const StaffPage = lazy(() =>
  import("@/features/operations/pages").then((m) => ({ default: m.StaffPage })),
);
const JumuaPage = lazy(() =>
  import("@/features/operations/pages").then((m) => ({ default: m.JumuaPage })),
);
const DonationsPage = lazy(() =>
  import("@/features/operations/pages").then((m) => ({
    default: m.DonationsPage,
  })),
);
const MaintenancePage = lazy(() =>
  import("@/features/operations/pages").then((m) => ({
    default: m.MaintenancePage,
  })),
);
const MadrasaFeesPage = lazy(() =>
  import("@/features/operations/pages").then((m) => ({
    default: m.MadrasaFeesPage,
  })),
);
/**
 * The three daily tasks, each a whole screen of its own.
 *
 * The committee came from a desktop package where "take maasavari", "enter a
 * family" and "write a marriage" were each one window, opened from a tile on
 * the home screen. These are those windows.
 */
const CollectPage = lazy(() =>
  import("@/features/finance/collect").then((m) => ({
    default: m.CollectPage,
  })),
);
const RegisterFamilyPage = lazy(() =>
  import("@/features/community/register-family").then((m) => ({
    default: m.RegisterFamilyPage,
  })),
);
const NewRegisterEntryPage = lazy(() =>
  import("@/features/registers/new-entry-page").then((m) => ({
    default: m.NewRegisterEntryPage,
  })),
);
const SettingsPage = lazy(() =>
  import("@/features/settings/page").then((m) => ({ default: m.SettingsPage })),
);
/**
 * The household portal is a whole second product, and the two audiences never
 * overlap: a family never opens a staff screen and staff never open this. Lazy
 * so neither pays for the other's code -- which matters most here, because a
 * household signs in on a phone over mobile data to check one number.
 */
const HouseholdPortal = lazy(() =>
  import("@/features/household/portal").then((m) => ({
    default: m.HouseholdPortal,
  })),
);

/**
 * What each route needs, in step with the sidebar's gating in `app-shell`.
 *
 * Detail routes inherit their list's permission, which is what the endpoints
 * behind them check. Settings is absent on purpose: every signed-in user can
 * reach their own account.
 */
const SECTION_PERMISSIONS: Record<string, Permission[]> = {
  "": ["dashboard.read"],
  mahallu: ["dashboard.read"],
  zakat: ["zakat.read"],
  palliative: ["palliative.read"],
  areas: ["area.read"],
  houses: ["house.read"],
  families: ["family.read"],
  people: ["person.read"],
  registers: ["register.read"],
  survey: ["survey.review"],
  madrasa: ["madrasa.read"],
  collection: ["maasavari.read"],
  collect: ["maasavari.read"],
  revenue: ["fund.general.read"],
  jumua: ["fund.general.read"],
  donations: ["fund.general.read"],
  staff: ["staff.read"],
  maintenance: ["fund.general.read"],
  "madrasa-fees": ["madrasa.fee.read"],
};

/**
 * Sub-routes that need more than their section.
 *
 * `/families/new` writes a house, a family and its people in one go, and
 * `/registers/new/<TYPE>` drafts an entry -- a role that may only read either
 * list must not be shown a form that will be refused on save.
 */
function subRouteNeeds(
  section: string,
  id: string | undefined,
): Permission[] | null {
  if (section === "families" && id === "new") return ["family.write"];
  if (section === "registers" && id === "new") return ["register.draft"];
  return null;
}

/**
 * Where to send someone whose role has no dashboard.
 *
 * Ordered as the sidebar is, so the landing screen is the first thing they
 * would have clicked anyway. A role without `dashboard.read` used to land on
 * the dashboard and sit on a loading skeleton forever.
 */
const LANDING_ORDER = [
  "/",
  "/families",
  "/houses",
  "/people",
  "/collect",
  "/jumua",
  "/registers",
  "/survey",
  "/madrasa",
  "/madrasa-fees",
  "/zakat",
  "/palliative",
  "/staff",
  "/areas",
  "/settings",
] as const;

const queryClient = createQueryClient();

function currentPath(): string {
  return window.location.hash.replace(/^#/, "") || "/";
}

/**
 * The route without its query string.
 *
 * Links like `#/families?q=P31` and `#/collect?house=P31` carry a query;
 * without this the section read as "families?q=P31", matched nothing, and
 * fell through to the default screen.
 */
function routeOf(path: string): string {
  const query = path.indexOf("?");
  return query === -1 ? path : path.slice(0, query);
}

function Router() {
  const { t } = useTranslation();
  const { session, status, hasRole, can } = useSession();
  const [path, setPath] = useState(currentPath);

  useEffect(() => {
    const onHashChange = () => setPath(currentPath());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  /**
   * Land somewhere this role can actually open.
   *
   * Only ever redirects away from the default route, and only when the role
   * cannot read the dashboard -- so nobody who lands correctly is moved, and a
   * path typed by hand is left alone to reach the refusal further down.
   */
  const landing = LANDING_ORDER.find((candidate) => {
    const needs = SECTION_PERMISSIONS[candidate.slice(1)];
    return !needs || can(...needs);
  });
  const misdirected =
    status === "authenticated" &&
    !hasRole("HOUSEHOLD") &&
    routeOf(path) === "/" &&
    !can("dashboard.read");
  useEffect(() => {
    if (misdirected && landing && landing !== "/") {
      window.location.hash = landing;
    }
  }, [misdirected, landing]);

  const navigate = (next: string) => {
    window.location.hash = next;
    setPath(next);
  };

  if (status === "loading") {
    return (
      <div className="grid min-h-dvh place-items-center bg-canvas">
        <p className="text-md text-ink-muted">{t("state.loading")}…</p>
      </div>
    );
  }

  if (status === "anonymous") return <LoginPage />;

  /**
   * A household gets its own root, not the staff shell.
   *
   * This is a correctness decision rather than a styling one. `AppShell` is
   * built around navigation this role has no permission to reach, and its
   * sidebar counts come from a community-wide summary endpoint that answers
   * 403 for a household -- so rendering it would wrap a permission error in a
   * frame of dead links. The backend refuses those routes regardless; this
   * just stops us asking.
   *
   * Checked before the active-mahallu guard below because a household always
   * has exactly one, and after the anonymous check because the role only
   * exists once there is a session to read it from.
   */
  if (hasRole("HOUSEHOLD")) {
    return (
      <Suspense
        fallback={
          <div className="grid min-h-dvh place-items-center bg-canvas">
            <p className="text-md text-ink-muted">{t("state.loading")}…</p>
          </div>
        }
      >
        <HouseholdPortal />
      </Suspense>
    );
  }

  if (misdirected) {
    return (
      <div className="grid min-h-dvh place-items-center bg-canvas">
        <p className="text-md text-ink-muted">{t("state.loading")}…</p>
      </div>
    );
  }

  if (!session?.active_mahallu_id) {
    return (
      <div className="grid min-h-dvh place-items-center bg-canvas px-4">
        <p className="max-w-sm text-center text-md text-ink-muted">
          {t("errors.NO_ACTIVE_MAHALLU")}
        </p>
      </div>
    );
  }

  // /houses/<id> and /families/<id> open the detail screens.
  const route = routeOf(path);
  const [, section, id, sub] = route.split("/");

  /**
   * What each section needs, matched to the sidebar's own gating.
   *
   * Typing or bookmarking a path the sidebar does not offer used to render
   * the screen anyway: the community lists came up around a permission error
   * with a live "Add" button, and the three dashboards sat on a loading
   * skeleton that never resolved.
   */
  const needed =
    subRouteNeeds(section ?? "", id) ?? SECTION_PERMISSIONS[section ?? ""];
  if (needed && !can(...needed)) {
    return (
      <AppShell currentPath={route} onNavigate={navigate}>
        <NoAccessState />
      </AppShell>
    );
  }
  const page =
    section === "areas" ? (
      <AreasPage />
    ) : section === "houses" ? (
      id ? (
        <HouseDetailPage houseId={id} onNavigate={navigate} />
      ) : (
        <HousesPage onOpen={(house) => navigate(`/houses/${house.id}`)} />
      )
    ) : section === "families" ? (
      id === "new" ? (
        <RegisterFamilyPage />
      ) : id ? (
        <FamilyDetailPage familyId={id} onNavigate={navigate} />
      ) : (
        <FamiliesPage onOpen={(family) => navigate(`/families/${family.id}`)} />
      )
    ) : section === "registers" ? (
      id === "new" ? (
        <NewRegisterEntryPage type={sub} />
      ) : id ? (
        <RegisterDetailPage entryId={id} onNavigate={navigate} />
      ) : (
        <RegistersPage onOpen={(entry) => navigate(`/registers/${entry.id}`)} />
      )
    ) : section === "collect" ? (
      <CollectPage />
    ) : section === "collection" ? (
      // The month-wise list. Plain #/collection (an old bookmark) is the
      // counter, which replaced the recent-collections page.
      id === "month" ? (
        <CollectionPage view={id} onNavigate={navigate} />
      ) : (
        <CollectPage />
      )
    ) : section === "survey" ? (
      <SurveyPage />
    ) : section === "madrasa" ? (
      <MadrasaPage onNavigate={navigate} />
    ) : section === "people" ? (
      id ? (
        <PersonDetailPage personId={id} onNavigate={navigate} />
      ) : (
        <PeoplePage onOpen={(person) => navigate(`/people/${person.id}`)} />
      )
    ) : section === "mahallu" ? (
      <MahalluDashboardPage onNavigate={navigate} />
    ) : section === "zakat" ? (
      <ZakatDashboardPage />
    ) : section === "palliative" ? (
      <PalliativeDashboardPage />
    ) : section === "revenue" ? (
      <RevenuePage />
    ) : section === "staff" ? (
      <StaffPage view={id} onNavigate={navigate} />
    ) : section === "jumua" ? (
      <JumuaPage />
    ) : section === "donations" ? (
      <DonationsPage />
    ) : section === "maintenance" ? (
      <MaintenancePage />
    ) : section === "madrasa-fees" ? (
      <MadrasaFeesPage />
    ) : section === "settings" ? (
      <SettingsPage />
    ) : (
      <MahalluDashboardPage onNavigate={navigate} />
    );

  return (
    <AppShell currentPath={route} onNavigate={navigate}>
      {/* The shell stays put while the page chunk arrives, so a route change
          does not blank the screen -- only the content area waits. */}
      <Suspense
        fallback={
          <div className="grid h-full place-items-center">
            <p className="text-md text-ink-muted">{t("state.loading")}…</p>
          </div>
        }
      >
        {/* Keyed by section, so the content settles in on a route change but
            not on every re-render within a screen. The quietest animation in
            the product on purpose: a screen that announces itself each time
            you open it stops being a tool. */}
        {/* A "new" form is keyed by its whole route, so going from a new birth
            to a new death starts a clean form rather than carrying the first
            one's half-typed fields across. */}
        <div
          key={id === "new" ? route : (section ?? "home")}
          className="h-full animate-rise"
        >
          {page}
        </div>
      </Suspense>
    </AppShell>
  );
}

export function App() {
  // The certificate QR code points at a real path, not a hash, and whoever
  // scanned it has no account here -- they are holding a piece of paper. So
  // this is decided above the session provider: mounting it would fire an
  // /auth/me the visitor can never satisfy, and the shell below it would
  // send a receiving mahallu to a login screen, which makes the printed QR
  // worse than useless.
  const certificateId = verificationIdFromPath(window.location.pathname);
  if (certificateId) return <VerifyPage certificateId={certificateId} />;

  // The public survey form, for the same reason and in the same place. The
  // household holding this link has no account either -- the link IS the
  // credential -- so mounting the session provider would fire an /auth/me
  // they can never satisfy and bounce them to a login screen.
  //
  // A real path rather than a hash, so the URL survives being pasted into a
  // chat message, and so nginx's try_files serves the app for it.
  const surveyToken = surveyTokenFromPath(window.location.pathname);
  if (surveyToken) {
    return (
      <Suspense fallback={<div className="min-h-dvh bg-canvas" />}>
        <PublicSurveyForm token={surveyToken} />
      </Suspense>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider queryClient={queryClient}>
        <Router />
        {/* Outside the router so a confirmation survives the navigation that
            often follows the write it is confirming. */}
        <Toaster />
      </SessionProvider>
    </QueryClientProvider>
  );
}
